import { createSelector } from 'reselect'
import { arrayToObject } from 'lib/array/arrayUtils'
import { getVillaViews } from '../../view/villa'
import { getCruiseItineraryCountryCodes } from 'checkout/lib/utils/cruises/itinerary'
import { getCheckoutCruiseOfferView } from '../../view/cruise'
import getBedbankItemViews from '../../view/itemViews/getBedbankItemViews'
import getHotelItemViews from '../../view/itemViews/getHotelItemViews'
import getTourV2ItemViews from '../../view/itemViews/getTourV2ItemViews'
import { getFlightItemsView } from '../../view/flights'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { isEsimExcludedCountry } from 'loyalty/lib/esims/esimGeoExclusion'

/**
 * Maps the full airports list (origin + destination) by code.
 * Unlike getAirportsByCode (which only maps originAirports), this includes
 * destination airports so we can resolve country codes for flight destinations.
 */
const getAllAirportsByCode = createSelector(
  (state: App.State) => state.geo.airports,
  (airports): Record<string, App.Airport> => arrayToObject(airports, airport => airport.code),
)

/**
 * Collects destination country codes from all checkout item verticals.
 * Shared logic with getIsCheckoutLoyaltyInternationalTrip — extracted here
 * so the eSIM selector can reuse it without duplicating the collection.
 */
export const getCheckoutDestinationCountryCodes = createSelector(
  (state: App.State) => getCheckoutCruiseOfferView(state),
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getAllAirportsByCode(state),
  (cruiseViews, bedbankViews, tourV2Views, hotelViews, villaViews, flightViews, airports): Array<string> => {
    const codes: Array<string> = []

    cruiseViews.data.forEach(view => {
      if (view.offer) {
        codes.push(...getCruiseItineraryCountryCodes(view.offer))
      }
    })
    bedbankViews.data.forEach(view => {
      if (view.offer?.property?.address?.countryCode) {
        codes.push(view.offer.property.address.countryCode)
      }
    })
    tourV2Views.data.forEach(view => {
      const departureIds = view.itemViews.map(v => v.item.purchasableOption.fkVariationId)
      departureIds.forEach(departureId => {
        const countries = view.offer?.variations[departureId]?.countriesVisited
        if (countries) codes.push(...countries)
      })
    })
    hotelViews.data.forEach(view => {
      const cc = view.offer?.property?.geoData?.countryCode
      if (cc) codes.push(cc)
    })
    villaViews.data.forEach(view => {
      const cc = view.offer?.property?.geoData?.countryCode
      if (cc) codes.push(cc)
    })
    flightViews.data.forEach(view => {
      const airport = airports[view.item.destinationAirportCode]
      if (airport?.countryCode) codes.push(airport.countryCode)
    })

    return codes
  },
)

/**
 * Whether the current checkout is an "international trip" for eSIM benefit purposes.
 * Uses loyalty account region (not geo) and eSIM excluded countries so checkout
 * visibility matches post-booking eligibility (esimOrderUtils / esimOfferEligibility).
 */
const getIsCheckoutEsimInternationalTrip = createSelector(
  getCheckoutDestinationCountryCodes,
  (state: App.State) => getLoyaltyAccountRegion(state),
  (state: App.State) => state.loyalty.config?.esimExcludedCountryCodes ?? [],
  (destinationCodes, accountRegion, esimExcludedCountryCodes): boolean => {
    if (!accountRegion) return false
    const regionLower = accountRegion.toLowerCase()
    return destinationCodes.some(cc =>
      cc.toLowerCase() !== regionLower && !isEsimExcludedCountry(cc, esimExcludedCountryCodes),
    )
  },
)

export default getIsCheckoutEsimInternationalTrip
